import {
	EcsCachedConfig,
	EcsConfigType,
	EcsResponseBase,
	IDynamicCampaignHostOptions,
	IEcsAdapter,
	IECSClientOptions,
} from '@ms-ofb/floodgate-shared-typings';
import * as SyncTasks from 'synctasks';
import SubscribableEvent from 'subscribableevent';
import SkypeEcsClient, { Models as SkypeEcsModels } from '@skype/ecsclient';
import * as Utils from './Utils';
import StorageProvider from './StorageProvider';
import DefaultFloodgateECSOptions from './DefaultConfig';
import { TelemetryEvents } from './TelemetryEvents';

export default class EcsClient<T extends EcsResponseBase> implements IEcsAdapter<T> {
	public telemetryEvents: SubscribableEvent<(eventName: TelemetryEvents, eventData: unknown) => void>;
	private ecsClient: SkypeEcsClient<T>;
	private _skypeClientConfig: SkypeEcsModels.ConfigModel<T>;

	constructor(dynamicCampaignOptions: IDynamicCampaignHostOptions) {
		let _storageProvider: StorageProvider;
		this.telemetryEvents = new SubscribableEvent<(eventName: TelemetryEvents, eventData: unknown) => void>();

		if (!Utils.isNOU(dynamicCampaignOptions.localStorageProvider)) {
			_storageProvider = new StorageProvider(dynamicCampaignOptions.localStorageProvider);
		} else {
			throw new Error("[Dynamic Campaign] - The Storage provider is not defined but it is a required field.");
		}

		dynamicCampaignOptions.filters = new Map([
			...Array.from(DefaultFloodgateECSOptions.filters.entries()),
			...Array.from(dynamicCampaignOptions.filters?.entries() ?? []),
		]);

		if (dynamicCampaignOptions.appName) {
			dynamicCampaignOptions.filters.set('Application', dynamicCampaignOptions.appName);
		}
		if (dynamicCampaignOptions.appVersion) {
			dynamicCampaignOptions.filters.set('version', dynamicCampaignOptions.appVersion);
		}
		if (dynamicCampaignOptions.language) {
			dynamicCampaignOptions.filters.set('language', dynamicCampaignOptions.language);
		}

		// Safety to mitigate human error in agents vs agent.
		if (dynamicCampaignOptions.filters.has('agents')) {
			if (!dynamicCampaignOptions.filters.has('agent')) {
				dynamicCampaignOptions.filters.set('agent', dynamicCampaignOptions.filters.get('agents'));
			}
			dynamicCampaignOptions.filters.delete('agents');
		}

		const floodgateEcsOptions: IECSClientOptions = { ...DefaultFloodgateECSOptions, ...dynamicCampaignOptions };

		this._skypeClientConfig = {
			hosts: [floodgateEcsOptions.host],
			clientName: floodgateEcsOptions.clientName,
			clientVersion: floodgateEcsOptions.clientVersion,
			configsToFetch: [EcsConfigType.Default],
			initialAppActiveState: true,
			fetchTimeout: floodgateEcsOptions.timeout,
			getEcsParameters: () => {
				const parameters: { [key: string]: string | number } = {};
				for (const key of floodgateEcsOptions.filters.keys()) {
					parameters[key] = floodgateEcsOptions.filters.get(key);
				}
				return SyncTasks.Resolved(parameters);
			},
			databaseInterface: _storageProvider,
			fetchAttemptMax: dynamicCampaignOptions.fetchAttemptMax || 10, // max refetch default at 10
		}

		this.ecsClient = new SkypeEcsClient<T>();
		this.ecsClient.setAllowBackgroundFetch(floodgateEcsOptions.allowBackgroundFetch);

		// Purposely using bogus Array.findIndex here so babel can detect and polyfill the function
		[].findIndex((x) => x === undefined);
	}

	async start(): Promise<void> {
		try {
			this.ecsClient.telemetryEventAdded.subscribe((eventName: TelemetryEvents, eventData: unknown) => {
				this.telemetryEvents.fire(eventName, eventData);
			});
			await this.ecsClient.initialize(this._skypeClientConfig).toEs6Promise();
		} catch (e) {
			this.telemetryEvents.fire(TelemetryEvents.EcsClientStartFailed, { ErrorMessage: e })
		}
	}

	getConfig(): EcsCachedConfig<T> {
		return this.ecsClient.getConfig();
	}

	queueManualRefresh(): void {
		this.ecsClient.requestUpdate();
	}

	pause(): void {
		this.ecsClient.pause();
	}

	resume(): void {
		this.ecsClient.resume();
	}
}
